<template>
  <main class="p-6 space-y-6">
    <div>
      <h2 class="text-2xl font-bold text-foreground">Paramètres</h2>
      <p class="text-muted-foreground">Configurez les paramètres généraux de la plateforme</p>
    </div>

    <div class="space-y-6">
      <!-- Tabs Navigation -->
      <div class="bg-muted p-1 rounded-lg inline-flex">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors',
            activeTab === tab.id
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          ]"
        >
          <svg
            v-if="tab.icon === 'globe'"
            class="h-4 w-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m9 9a9 9 0 01-9 9" />
          </svg>
          <svg
            v-else-if="tab.icon === 'bell'"
            class="h-4 w-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <svg
            v-else-if="tab.icon === 'shield'"
            class="h-4 w-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <svg
            v-else-if="tab.icon === 'palette'"
            class="h-4 w-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content -->
      <div>
        <!-- General Settings -->
        <div v-if="activeTab === 'general'" class="grid gap-6">
          <div class="card-base shadow-elegant">
            <div class="p-6 pb-4">
              <h3 class="text-lg font-semibold">Informations du site</h3>
              <p class="text-sm text-muted-foreground mt-1">Paramètres généraux de votre plateforme</p>
            </div>
            <div class="p-6 pt-0 space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-2">Nom du site</label>
                  <input
                    v-model="settings.siteName"
                    class="input-base"
                    placeholder="Nom de votre site"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium mb-2">Email de contact</label>
                  <input
                    v-model="settings.contactEmail"
                    type="email"
                    class="input-base"
                    placeholder="contact@exemple.com"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Description</label>
                <textarea
                  v-model="settings.siteDescription"
                  rows="2"
                  class="input-base"
                  placeholder="Description de votre plateforme"
                ></textarea>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-2">Téléphone</label>
                  <input
                    v-model="settings.contactPhone"
                    class="input-base"
                    placeholder="+33 1 23 45 67 89"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium mb-2">Adresse</label>
                  <input
                    v-model="settings.address"
                    class="input-base"
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="card-base shadow-elegant">
            <div class="p-6 pb-4">
              <h3 class="text-lg font-semibold">Réseaux sociaux</h3>
              <p class="text-sm text-muted-foreground mt-1">Liens vers vos pages sociales</p>
            </div>
            <div class="p-6 pt-0 space-y-4">
              <div>
                <label class="block text-sm font-medium mb-2">Facebook</label>
                <input
                  v-model="settings.facebook"
                  class="input-base"
                  placeholder="https://facebook.com/votre-page"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Twitter</label>
                <input
                  v-model="settings.twitter"
                  class="input-base"
                  placeholder="https://twitter.com/votre-compte"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">LinkedIn</label>
                <input
                  v-model="settings.linkedin"
                  class="input-base"
                  placeholder="https://linkedin.com/company/votre-entreprise"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Notifications Settings -->
        <div v-else-if="activeTab === 'notifications'">
          <div class="card-base shadow-elegant">
            <div class="p-6 pb-4">
              <h3 class="text-lg font-semibold">Préférences de notification</h3>
              <p class="text-sm text-muted-foreground mt-1">Gérez vos alertes et rapports</p>
            </div>
            <div class="p-6 pt-0 space-y-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium">Notifications par email</p>
                  <p class="text-sm text-muted-foreground">Recevoir des emails pour les événements importants</p>
                </div>
                <button
                  @click="settings.emailNotifications = !settings.emailNotifications"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    settings.emailNotifications ? 'bg-primary' : 'bg-muted'
                  ]"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
              </div>
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium">Alertes de dons</p>
                  <p class="text-sm text-muted-foreground">Notification immédiate pour chaque nouveau don</p>
                </div>
                <button
                  @click="settings.donationAlerts = !settings.donationAlerts"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    settings.donationAlerts ? 'bg-primary' : 'bg-muted'
                  ]"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      settings.donationAlerts ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
              </div>
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium">Rapport hebdomadaire</p>
                  <p class="text-sm text-muted-foreground">Résumé des activités chaque semaine</p>
                </div>
                <button
                  @click="settings.weeklyReport = !settings.weeklyReport"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    settings.weeklyReport ? 'bg-primary' : 'bg-muted'
                  ]"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      settings.weeklyReport ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Security Settings -->
        <div v-else-if="activeTab === 'security'">
          <div class="card-base shadow-elegant">
            <div class="p-6 pb-4">
              <h3 class="text-lg font-semibold">Paramètres de sécurité</h3>
              <p class="text-sm text-muted-foreground mt-1">Options de sécurité et d'accès</p>
            </div>
            <div class="p-6 pt-0 space-y-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium">Mode maintenance</p>
                  <p class="text-sm text-muted-foreground">Désactiver l'accès public au site</p>
                </div>
                <button
                  @click="settings.maintenanceMode = !settings.maintenanceMode"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    settings.maintenanceMode ? 'bg-primary' : 'bg-muted'
                  ]"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      settings.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
              </div>
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium">Inscriptions ouvertes</p>
                  <p class="text-sm text-muted-foreground">Permettre les nouvelles inscriptions</p>
                </div>
                <button
                  @click="settings.registrationOpen = !settings.registrationOpen"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    settings.registrationOpen ? 'bg-primary' : 'bg-muted'
                  ]"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      settings.registrationOpen ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Appearance Settings -->
        <div v-else-if="activeTab === 'appearance'">
          <div class="card-base shadow-elegant">
            <div class="p-6 pb-4">
              <h3 class="text-lg font-semibold">Apparence</h3>
              <p class="text-sm text-muted-foreground mt-1">Personnalisez l'apparence de votre site</p>
            </div>
            <div class="p-6 pt-0 space-y-4">
              <div>
                <label class="block text-sm font-medium mb-2">Couleur principale</label>
                <div class="flex items-center gap-4 mt-2">
                  <div class="h-10 w-10 rounded-lg bg-primary border-2 border-primary-foreground/20"></div>
                  <input
                    value="#005057"
                    disabled
                    class="input-base max-w-[120px] bg-muted"
                  />
                  <span class="text-sm text-muted-foreground">Teal Stag Fondation</span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Mode sombre</label>
                <div class="flex items-center gap-4 mt-2">
                  <button
                    @click="toggleTheme"
                    :class="[
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                      darkMode ? 'bg-primary' : 'bg-muted'
                    ]"
                  >
                    <span
                      :class="[
                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                        darkMode ? 'translate-x-6' : 'translate-x-1'
                      ]"
                    />
                  </button>
                  <span class="text-sm text-muted-foreground">
                    {{ darkMode ? 'Mode sombre activé' : 'Mode sombre désactivé' }}
                  </span>
                </div>
              </div>
              <p class="text-sm text-muted-foreground">
                L'apparence est définie par la charte graphique Stag Fondation. Vous pouvez seulement activer ou désactiver le mode sombre.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
      <button
        @click="saveSettings"
        class="btn-primary flex items-center px-6 py-3 rounded-lg"
      >
        <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
        </svg>
        Sauvegarder les modifications
      </button>
    </div>

    <!-- Toast Notification -->
    <div
      v-if="toastMessage"
      class="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-3 rounded-lg shadow-lg z-50"
    >
      {{ toastMessage }}
    </div>
</main>
</template>

<script>
export default {
  name: 'AdminSettings',
  data() {
    return {
      activeTab: 'general',
      darkMode: false,
      toastMessage: '',
      tabs: [
        { id: 'general', label: 'Général', icon: 'globe' },
        { id: 'notifications', label: 'Notifications', icon: 'bell' },
        { id: 'security', label: 'Sécurité', icon: 'shield' },
        { id: 'appearance', label: 'Apparence', icon: 'palette' }
      ],
      settings: {
        siteName: 'Stag Fondation',
        siteDescription: 'Connecter, Former, Transformer - Pour un monde numérique inclusif',
        contactEmail: 'contact@stagfondation.org',
        contactPhone: '+33 1 23 45 67 89',
        address: '123 Avenue de l\'Innovation, 75001 Paris',
        facebook: 'https://facebook.com/stagfondation',
        twitter: 'https://twitter.com/stagfondation',
        linkedin: 'https://linkedin.com/company/stagfondation',
        emailNotifications: true,
        donationAlerts: true,
        weeklyReport: true,
        maintenanceMode: false,
        registrationOpen: true
      }
    };
  },
  methods: {
    saveSettings() {
      // Sauvegarder dans localStorage
      localStorage.setItem('adminSettings', JSON.stringify(this.settings));
      
      // Afficher notification
      this.showToast('Paramètres sauvegardés avec succès');
      
      // Mettre à jour le mode sombre si nécessaire
      if (this.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    },
    toggleTheme() {
      this.darkMode = !this.darkMode;
    },
    showToast(message) {
      this.toastMessage = message;
      setTimeout(() => {
        this.toastMessage = '';
      }, 3000);
    },
    loadSettings() {
      const savedSettings = localStorage.getItem('adminSettings');
      if (savedSettings) {
        this.settings = JSON.parse(savedSettings);
      }
      
      // Charger le mode sombre
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'dark') {
        this.darkMode = true;
        document.documentElement.classList.add('dark');
      }
    }
  },
  mounted() {
    this.loadSettings();
    
    // Sauvegarder automatiquement en quittant la page
    window.addEventListener('beforeunload', () => {
      this.saveSettings();
    });
  },
  watch: {
    darkMode(newVal) {
      localStorage.setItem('theme', newVal ? 'dark' : 'light');
      if (newVal) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }
};
</script>

<style scoped>
/* Animations pour les cartes */
.card-base {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-base:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px hsla(183, 100%, 17%, 0.16);
}

/* Animation pour les onglets */
.tab-button {
  transition: all 0.2s ease;
}

.tab-button:hover:not(.active) {
  background-color: hsl(var(--muted));
}

/* Animation pour les switches */
.switch-thumb {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.switch-track {
  transition: background-color 0.2s ease;
}

/* Animation pour le toast */
.toast-enter-active {
  animation: toastSlideIn 0.3s ease;
}

.toast-leave-active {
  animation: toastSlideOut 0.3s ease;
}

@keyframes toastSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes toastSlideOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
}

/* Animation pour le contenu des onglets */
.tab-content-enter-active {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Style pour les inputs désactivés */
input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Style pour les badges d'onglets */
.tab-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s;
}

.tab-badge.active {
  background-color: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
}

.tab-badge:not(.active) {
  background-color: transparent;
  color: hsl(var(--muted-foreground));
}

/* Animation pour les transitions de couleur */
.color-transition {
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}

/* Style pour les sections */
.section-divider {
  border-color: hsl(var(--border));
  margin: 1.5rem 0;
}

/* Responsive design */
@media (max-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .flex.justify-between {
    flex-direction: column;
    gap: 1rem;
  }
  
  .flex.justify-between > div:first-child {
    margin-bottom: 0.5rem;
  }
}
</style>